// src/components/filters/utils/FilterUtils.ts
import { FilterModel } from 'ag-grid-community';
import { FilterOption } from '@amzn/controllershiphub-typescript-client';

interface FilterPreference {
  filterType: string;
  filterModels: Array<FilterModel>;
  displayName?: string;
}

export const extractSelectedValues = (filterModel: FilterModel): Record<string, string[]> => {
  return Object.entries(filterModel).reduce(
    (acc, [key, value]) => {
      if (value?.filterModels) {
        const selectedValues = value.filterModels
          .filter((model: FilterModel | null): model is FilterModel => Boolean(model))
          .reduce((values: string[], model: FilterModel) => {
            if (model.filterType === 'set' && model.values) {
              values.push(...model.values);
            } else if (model.filterType === 'text' && model.filter) {
              values.push(model.filter);
            }
            return values;
          }, []);

        if (selectedValues.length) {
          acc[key] = selectedValues;
        }
      }
      return acc;
    },
    {} as Record<string, string[]>
  );
};

export const processAgGridFilters = (filters: FilterModel): Record<string, FilterPreference> => {
  return Object.entries(filters).reduce(
    (acc, [key, value]) => {
      if (value?.filterModels?.length) {
        const selectedValues = value.filterModels
          .filter(
            (model: any): model is FilterModel =>
              model !== null && model.filterType === 'set' && Array.isArray(model.values)
          )
          .flatMap((model: FilterModel) => model.values)
          .filter((value: string | null): value is string => value !== null);

        if (selectedValues.length) {
          acc[key] = {
            filterType: 'multi',
            filterModels: [
              {
                filterType: 'set',
                values: selectedValues,
              },
            ],
          };
        }
      }
      return acc;
    },
    {} as Record<string, FilterPreference>
  );
};

export const formatDisplayNames = (
  filters: Record<string, any>,
  displayNameMapping: Record<string, string>
): Record<string, any> => {
  return Object.entries(filters).reduce<Record<string, any>>((acc, [key, values]) => {
    const displayName = displayNameMapping[key] || key;
    acc[displayName] = values;
    return acc;
  }, {});
};

export const createEmptyFilters = (
  options: Record<string, FilterOption>
): Record<string, string[]> =>
  Object.keys(options).reduce<Record<string, string[]>>((acc, key) => {
    acc[key] = [];
    return acc;
  }, {});

export const compareFilters = (
  filters1: Record<string, string[]>,
  filters2: Record<string, string[]>
): boolean => {
  const keys = new Set([...Object.keys(filters1), ...Object.keys(filters2)]);
  return Array.from(keys).some((key) => {
    const values1 = filters1[key] || [];
    const values2 = filters2[key] || [];
    return JSON.stringify(values1.sort()) !== JSON.stringify(values2.sort());
  });
};

export const processDistinctValues = (
  distinctValues: Record<string, string[]>,
  displayNameMapping: Record<string, string>
): Record<string, string[]> => {
  return Object.entries(distinctValues).reduce<Record<string, string[]>>((acc, [key, values]) => {
    const displayName = displayNameMapping[key] || key;
    if (values) {
      acc[displayName] = values;
    }
    return acc;
  }, {});
};

export const convertToFilterPreference = (values: string[]): FilterPreference => ({
  filterType: 'multi',
  filterModels: [
    {
      filterType: 'set',
      values,
    },
  ],
});
